import { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '@/services/authConfig';
import {
  getEval,
  listEvalRuns,
  startEvalRun,
  cancelEvalRun,
  updateEval,
  listModels,
  getAgentVersions,
} from '@/services/evalApi';

export interface EvalFunction {
  name: string;
  function: string;
  config?: Record<string, unknown>;
}

export interface Scenario {
  [key: string]: string | number | boolean;
}

export interface JudgeCriterion {
  name: string;
  weight: number;
  description?: string;
}

export interface JudgeFunction {
  name: string;
  function: string;
  config?: Record<string, unknown>;
}

export interface JudgeConfig {
  agent: string;
  pass_threshold?: number;
  criteria?: JudgeCriterion[];
  function?: JudgeFunction;
}

export interface Variant {
  name: string;
  model?: string | { name: string };
  temperature?: number;
  agent_version?: number;
}

export interface OptimizationObjective {
  name: string;
  source: string;
  direction: string;
}

export interface EvalData {
  id: string;
  name: string;
  description?: string;
  agent?: string;
  variants?: Variant[];
  judge?: JudgeConfig;
  metrics?: string[];
  function?: EvalFunction;
  eval_functions?: EvalFunction[];
  inputs?: { scenarios?: Scenario[] };
  optimization?: {
    enabled: boolean;
    objectives?: OptimizationObjective[];
    directives?: string[];
    goal_description?: string;
    limits?: {
      max_iterations?: number;
      trials_per_iteration?: number;
    };
  };
}

export interface OptimizationStatus {
  run_id?: string;
  status: string;
  current_iteration: number;
  max_iterations: number;
  total_eval_runs: number;
  max_eval_runs: number;
  baseline_values: Record<string, number>;
  current_best_values: Record<string, number>;
  best_iteration?: number;
  exploration_mode: string;
  pareto_size: number;
  pending_checkpoint_id?: string;
}

export interface ParetoSolution {
  config_hash: string;
  objective_values: Record<string, number>;
  iteration: number;
  is_best: boolean;
}

export interface ProposedChange {
  type: string;
  description: string;
  before?: string;
  after?: string;
}

export interface OptimizationIteration {
  iteration: number;
  objective_values: Record<string, number>;
  is_on_pareto: boolean;
  exploration_mode: string;
  proposed_changes?: ProposedChange[] | null;
  optimizer_analysis?: string | null;
  completed_at?: string | null;
  eval_run_id?: string | null;
}

export interface OptimizationRunSummary {
  run_id: string;
  status: string;
  current_iteration: number;
  max_iterations: number;
  best_iteration?: number;
  baseline_values: Record<string, number>;
  best_values: Record<string, number>;
  improvement_percent?: number;
  started_at?: string;
  completed_at?: string;
  is_current: boolean;
}

export interface EvalRun {
  id: string;
  status: string;
  completed_trials: number;
  total_trials: number;
  failed_trials?: number;
  started_at?: string;
  duration_ms?: number;
  variant_id?: string;
  variant_name?: string;
}

export interface ModelOption {
  value: string;
  label: string;
  provider: string;
}

export interface AgentVersion {
  version_number: number;
  commit_message?: string;
  is_draft: boolean;
}

export interface UseEvalDetailOptions {
  evalId: string | undefined;
  onError?: (message: string) => void;
}

export interface UseEvalDetailReturn {
  // Data
  evalData: EvalData | null;
  runs: EvalRun[];
  optStatus: OptimizationStatus | null;
  pareto: ParetoSolution[];
  iterations: OptimizationIteration[];
  models: ModelOption[];
  agentVersions: AgentVersion[];
  optimizationRuns: OptimizationRunSummary[];
  selectedRunId: string | null;

  // Loading states
  loading: boolean;
  runningEval: boolean;
  startingOptimization: boolean;

  // Error
  error: string | null;
  setError: (error: string | null) => void;

  // Actions
  fetchData: () => Promise<void>;
  fetchOptimizationData: (runId?: string) => Promise<void>;
  fetchOptimizationRuns: () => Promise<void>;
  selectOptimizationRun: (runId: string | null) => void;
  handleStartOptimization: () => Promise<void>;
  handlePauseOptimization: () => Promise<void>;
  handleResumeOptimization: () => Promise<void>;
  handleRun: () => Promise<void>;
  handleCancel: (runId: string) => Promise<void>;
  updateVariant: (
    variantIdx: number,
    updates: Partial<Variant>
  ) => Promise<void>;
  loadModels: () => Promise<void>;
  loadAgentVersions: () => Promise<void>;
}

export function useEvalDetail({
  evalId,
  onError,
}: UseEvalDetailOptions): UseEvalDetailReturn {
  // Core data state
  const [evalData, setEvalData] = useState<EvalData | null>(null);
  const [runs, setRuns] = useState<EvalRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [runningEval, setRunningEval] = useState(false);

  // Optimization state
  const [optStatus, setOptStatus] = useState<OptimizationStatus | null>(null);
  const [pareto, setPareto] = useState<ParetoSolution[]>([]);
  const [iterations, setIterations] = useState<OptimizationIteration[]>([]);
  const [startingOptimization, setStartingOptimization] = useState(false);
  const [optimizationRuns, setOptimizationRuns] = useState<OptimizationRunSummary[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  // Editing state
  const [models, setModels] = useState<ModelOption[]>([]);
  const [agentVersions, setAgentVersions] = useState<AgentVersion[]>([]);

  const setErrorWithCallback = useCallback(
    (message: string | null) => {
      setError(message);
      if (message && onError) {
        onError(message);
      }
    },
    [onError]
  );

  const fetchOptimizationRuns = useCallback(async () => {
    if (!evalId) return;
    try {
      const res = await fetch(`/api/evals/${evalId}/optimization/runs`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (res.ok) {
        const runs = await res.json();
        setOptimizationRuns(runs);
      }
    } catch (err) {
      console.error('Failed to fetch optimization runs:', err);
    }
  }, [evalId]);

  const fetchOptimizationData = useCallback(async (runId?: string) => {
    if (!evalId) return;
    try {
      const runIdParam = runId ? `?run_id=${runId}` : '';
      const [statusRes, paretoRes, iterRes] = await Promise.all([
        fetch(`/api/evals/${evalId}/optimization/status${runIdParam}`, {
          headers: getAuthHeaders(),
          credentials: 'include',
        }),
        fetch(`/api/evals/${evalId}/optimization/pareto${runIdParam}`, {
          headers: getAuthHeaders(),
          credentials: 'include',
        }),
        fetch(`/api/evals/${evalId}/optimization/iterations${runIdParam}`, {
          headers: getAuthHeaders(),
          credentials: 'include',
        }),
      ]);

      if (statusRes.ok) {
        const status = await statusRes.json();
        setOptStatus(status);
      }
      if (paretoRes.ok) {
        setPareto(await paretoRes.json());
      }
      if (iterRes.ok) {
        const data = await iterRes.json();
        setIterations(data.iterations || []);
      }
    } catch (err) {
      console.error('Failed to fetch optimization data:', err);
    }
  }, [evalId]);

  const selectOptimizationRun = useCallback((runId: string | null) => {
    setSelectedRunId(runId);
    fetchOptimizationData(runId || undefined);
  }, [fetchOptimizationData]);

  const fetchData = useCallback(async () => {
    if (!evalId) return;
    try {
      setLoading(true);
      const [evalInfo, runsList] = await Promise.all([
        getEval(evalId),
        listEvalRuns(evalId),
      ]);
      setEvalData(evalInfo);
      setRuns(runsList);

      // Fetch optimization data if optimization mode is enabled
      if (evalInfo.optimization?.enabled) {
        await Promise.all([
          fetchOptimizationData(),
          fetchOptimizationRuns(),
        ]);
      }

      setError(null);
    } catch (err) {
      setErrorWithCallback(
        err instanceof Error ? err.message : 'Failed to fetch data'
      );
    } finally {
      setLoading(false);
    }
  }, [evalId, fetchOptimizationData, fetchOptimizationRuns, setErrorWithCallback]);

  const handleStartOptimization = useCallback(async () => {
    if (!evalId) return;
    try {
      setStartingOptimization(true);
      const res = await fetch(`/api/evals/${evalId}/optimization/start`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to start optimization');
      }
      await fetchOptimizationData();
    } catch (err) {
      setErrorWithCallback(
        `Failed to start optimization: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    } finally {
      setStartingOptimization(false);
    }
  }, [evalId, fetchOptimizationData, setErrorWithCallback]);

  const handlePauseOptimization = useCallback(async () => {
    if (!evalId) return;
    try {
      const res = await fetch(`/api/evals/${evalId}/optimization/pause`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error('Failed to pause optimization');
      }
      await fetchOptimizationData();
    } catch (err) {
      setErrorWithCallback(
        `Failed to pause: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    }
  }, [evalId, fetchOptimizationData, setErrorWithCallback]);

  const handleResumeOptimization = useCallback(async () => {
    if (!evalId) return;
    try {
      const res = await fetch(`/api/evals/${evalId}/optimization/resume`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to resume optimization');
      }
      await fetchOptimizationData();
    } catch (err) {
      setErrorWithCallback(
        `Failed to resume: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    }
  }, [evalId, fetchOptimizationData, setErrorWithCallback]);

  const handleRun = useCallback(async () => {
    if (!evalId) return;
    try {
      setRunningEval(true);
      if (evalData?.optimization?.enabled) {
        await handleStartOptimization();
      } else {
        await startEvalRun(evalId, {});
      }
      await fetchData();
    } catch (err) {
      setErrorWithCallback(
        `Failed to start run: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    } finally {
      setRunningEval(false);
    }
  }, [evalId, evalData, handleStartOptimization, fetchData, setErrorWithCallback]);

  const handleCancel = useCallback(
    async (runId: string) => {
      try {
        await cancelEvalRun(runId);
        await fetchData();
      } catch (err) {
        setErrorWithCallback(
          `Failed to cancel run: ${err instanceof Error ? err.message : 'Unknown error'}`
        );
      }
    },
    [fetchData, setErrorWithCallback]
  );

  const updateVariant = useCallback(
    async (variantIdx: number, updates: Partial<Variant>) => {
      if (!evalData) return;

      const updatedVariants = evalData.variants?.map((v, idx) => {
        if (idx !== variantIdx) return v;
        return { ...v, ...updates };
      });

      await updateEval(evalData.id, {
        ...evalData,
        variants: updatedVariants,
      });

      await fetchData();
    },
    [evalData, fetchData]
  );

  const loadModels = useCallback(async () => {
    if (models.length > 0) return;
    try {
      const modelList = await listModels();
      setModels(modelList);
    } catch (err) {
      console.error('Failed to load models:', err);
      // Fallback models
      setModels([
        {
          value: 'openrouter:anthropic/claude-sonnet-4.5',
          label: 'Claude Sonnet 4.5',
          provider: 'openrouter',
        },
        {
          value: 'openrouter:openai/gpt-4o',
          label: 'GPT-4o',
          provider: 'openrouter',
        },
      ]);
    }
  }, [models.length]);

  const loadAgentVersions = useCallback(async () => {
    if (!evalData?.agent || agentVersions.length > 0) return;
    try {
      const versionsData = await getAgentVersions(evalData.agent);
      setAgentVersions(versionsData.versions || []);
    } catch (err) {
      console.error('Failed to load agent versions:', err);
    }
  }, [evalData?.agent, agentVersions.length]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Polling for updates
  useEffect(() => {
    const interval = setInterval(() => {
      // Refresh runs if any are running
      if (runs.some((r) => r.status === 'running') && evalId) {
        listEvalRuns(evalId).then(setRuns).catch(console.error);
      }
      // Refresh optimization data if active
      if (optStatus && ['running', 'paused'].includes(optStatus.status)) {
        fetchOptimizationData();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [evalId, runs, optStatus?.status, fetchOptimizationData]);

  return {
    // Data
    evalData,
    runs,
    optStatus,
    pareto,
    iterations,
    models,
    agentVersions,
    optimizationRuns,
    selectedRunId,

    // Loading states
    loading,
    runningEval,
    startingOptimization,

    // Error
    error,
    setError: setErrorWithCallback,

    // Actions
    fetchData,
    fetchOptimizationData,
    fetchOptimizationRuns,
    selectOptimizationRun,
    handleStartOptimization,
    handlePauseOptimization,
    handleResumeOptimization,
    handleRun,
    handleCancel,
    updateVariant,
    loadModels,
    loadAgentVersions,
  };
}
